import { InputRef } from 'antd';
import { KeyboardEvent, ClipboardEvent, useCallback, useEffect, useRef, useState } from 'react';

type TUseTypedMaskOptions = {
  format: (digits: string) => string;
  normalizeDigits?: (digits: string) => string;
  maxDigits: number;
  value?: string;
  onChange?: (value: string) => void;
};

export const useTypedMask = ({ format, maxDigits, value, onChange, normalizeDigits = (digits) => digits }: TUseTypedMaskOptions) => {
  const [digits, setDigits] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const digitsRef = useRef('');
  const inputRef = useRef<InputRef>(null);
  const lastReportedRef = useRef('');

  useEffect(() => {
    if (isFocused) return;

    const incoming = value ?? '';
    if (incoming === lastReportedRef.current) return;

    const nextDigits = incoming.replace(/\D/g, '').slice(0, maxDigits);
    digitsRef.current = nextDigits;
    setDigits(nextDigits);

    const formatted = nextDigits.length > 0 ? format(nextDigits) : '';
    lastReportedRef.current = formatted;
    if (formatted !== incoming) {
      onChange?.(formatted);
    }
  }, [value, isFocused, maxDigits, format, onChange]);

  const placeCursorAtFirstBlank = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current?.input;
      if (!el) return;
      const idx = el.value.indexOf('_');
      const pos = idx === -1 ? el.value.length : idx;
      el.setSelectionRange(pos, pos);
    });
  }, []);

  const report = useCallback((newDigits: string) => {
    digitsRef.current = newDigits;
    setDigits(newDigits);
    const reported = newDigits.length > 0 ? format(newDigits) : '';
    lastReportedRef.current = reported;
    onChange?.(reported);
  }, [format, onChange]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digitsRef.current.length > 0) report(digitsRef.current.slice(0, -1));
      placeCursorAtFirstBlank();
      return;
    }
    if (/^[0-9]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      if (digitsRef.current.length < maxDigits) report(digitsRef.current + e.key);
      placeCursorAtFirstBlank();
    }
  }, [maxDigits, report, placeCursorAtFirstBlank]);

  const onPaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData?.getData('text') ?? '';
    const normalized = normalizeDigits(pasted.replace(/\D/g, ''));
    if (!normalized.length) return;
    report((digitsRef.current + normalized).slice(0, maxDigits));
    placeCursorAtFirstBlank();
  }, [maxDigits, normalizeDigits, report, placeCursorAtFirstBlank]);

  const onFocus = useCallback(() => {
    setIsFocused(true);
    placeCursorAtFirstBlank();
  }, [placeCursorAtFirstBlank]);

  const onBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const displayValue = isFocused || digits.length > 0 ? format(digits) : '';

  return { inputRef, displayValue, onFocus, onBlur, onKeyDown, onPaste };
};
